<template>
  <div class="topic-management-page">
    <h2 class="page-title">话题管理</h2>

    <!-- 搜索区域 -->
    <div class="search-bar">
      <el-input v-model="searchForm.topic_name" placeholder="请输入话题名称" clearable size="large"
        style="width: 300px; margin-right: 15px" @keyup.enter="handleSearch">
        <template #prefix>
          <el-icon>
            <Search />
          </el-icon>
        </template>
      </el-input>

      <el-button type="primary" size="large" @click="handleSearch">
        <el-icon>
          <Search />
        </el-icon>
        搜索
      </el-button>

      <el-button type="success" size="large" @click="openAddDialog" class="add-btn">
        <el-icon>
          <Plus />
        </el-icon>
        新增话题
      </el-button>
    </div>

    <!-- 话题列表 -->
    <div class="table-wrapper">
      <el-table :data="titleList" stripe v-loading="loading" class="topic-table">
        <el-table-column prop="topic_name" label="话题名称" width="180" />
        <el-table-column prop="topic_type" label="写作类型" width="120">
          <template #default="{ row }">
            <el-tag :type="row.topic_type === 1 ? 'primary' : 'success'">
              {{ row.topic_type === 1 ? '手动导入' : 'AI生成' }}
            </el-tag>
          </template>
        </el-table-column>

        <el-table-column prop="topic_keyword" label="话题关键词" width="180">
          <template #default="{ row }">
            <span v-if="row.topic_type === 2">{{ row.topic_keyword }}</span>
            <span v-else>-</span>
          </template>
        </el-table-column>
        <el-table-column prop="topic_limit" label="生成上限" width="120">
          <template #default="{ row }">
            <span v-if="row.topic_type === 2">{{ row.topic_limit }}</span>
            <span v-else>-</span>
          </template>
        </el-table-column>

        <!-- AI来源显示 -->
        <el-table-column label="AI来源" width="120">
          <template #default="{ row }">
            <el-tag v-if="row.ai_source" :type="getAiSourceType(row.ai_source)" size="small">
              {{ getAiSourceText(row.ai_source) }}
            </el-tag>
            <span v-else>-</span>
          </template>
        </el-table-column>

        <!-- 话题列表 -->
        <el-table-column label="话题列表" width="150" fixed="right">
          <template #default="scope">
            <el-button type="info" size="small" @click="openTitleListDialog(scope.row)">
              <el-icon>
                <View />
              </el-icon>
              查看话题
            </el-button>
          </template>
        </el-table-column>

        <!-- AI生成 -->
        <el-table-column label="AI生成" width="150" fixed="right">
          <template #default="scope">
            <el-button v-if="scope.row.topic_type === 2" type="primary" size="small"
              @click="openAICreateDialog(scope.row)">
              <el-icon>
                <MagicStick />
              </el-icon>
              查看AI生成
            </el-button>
            <span v-else>-</span>
          </template>
        </el-table-column>
        <el-table-column prop="notes" label="备注" width="150" />
        <el-table-column prop="create_time" label="创建时间" width="180" />
        <el-table-column prop="update_time" label="更新时间" width="180" />

        <el-table-column label="操作" width="220" fixed="right">
          <template #default="scope">
            <el-button type="primary" size="small" @click="openEditDialog(scope.row)">
              <el-icon>
                <Edit />
              </el-icon>
              编辑
            </el-button>
            <el-button type="danger" size="small" @click="handleDelete(scope.row.id)">
              <el-icon>
                <Delete />
              </el-icon>
              删除
            </el-button>
          </template>
        </el-table-column>
      </el-table>
    </div>

    <!-- 分页组件 -->
    <div class="pagination-container">
      <el-pagination v-model:current-page="pagination.page" v-model:page-size="pagination.pageSize"
        :total="pagination.total" :page-sizes="[10, 20, 30, 50]" layout="total, sizes, prev, pager, next, jumper"
        @size-change="fetchTitleList" @current-change="fetchTitleList" />
    </div>

    <!-- 查看话题对话框 -->
    <el-dialog v-model="titleListDialogVisible" :title="titleListDialogTitle" width="60%" top="5vh"
      :close-on-click-modal="false">
      <TitleAiCrateView v-if="titleListDialogVisible && currentTitleType === 2" :title-id="currentTitleId"
        :store-id="userInfo.id" />
      <TitleListView v-else-if="titleListDialogVisible" :title-id="currentTitleId" :store-id="userInfo.id" />
    </el-dialog>

    <!-- 查看AI生成对话框 -->
    <el-dialog v-model="aiCreateDialogVisible" :title="aiCreateDialogTitle" width="60%" top="5vh"
      :close-on-click-modal="false">
      <AICreateView v-if="aiCreateDialogVisible" :title-id="currentTitleId" :store-id="userInfo.id" />
    </el-dialog>

    <!-- 新增/编辑对话框 -->
    <el-dialog v-model="dialogVisible" :title="dialogTitle" width="600px" :close-on-click-modal="false">
      <el-form ref="titleForm" :model="titleForm" label-width="120px" :rules="formRules">
        <el-form-item label="话题名称" prop="topic_name">
          <el-input v-model="titleForm.topic_name" placeholder="请输入话题名称" size="large" />
        </el-form-item>

        <el-form-item label="写作类型" prop="topic_type">
          <el-radio-group v-model="titleForm.topic_type" @change="handleTypeChange">
            <el-radio :label="1">手动导入</el-radio>
            <el-radio :label="2">AI生成</el-radio>
          </el-radio-group>
        </el-form-item>

        <!-- AI配置来源显示 -->
        <el-form-item v-if="titleForm.topic_type === 2 && aiConfigInfo" label="AI接口来源">
          <el-tag :type="getAiSourceType(aiConfigInfo.source)" size="large">
            {{ getAiSourceText(aiConfigInfo.source) }}
            <el-tooltip :content="`模型: ${aiConfigInfo.ai_model}`" placement="top">
              <el-icon style="margin-left: 4px; cursor: pointer;">
                <InfoFilled />
              </el-icon>
            </el-tooltip>
          </el-tag>
        </el-form-item>

        <!-- 是否润色 - 只在手动导入时显示 -->
        <el-form-item label="是否润色" prop="polish_open" v-if="titleForm.topic_type === 1">
          <el-radio-group v-model="titleForm.polish_open">
            <el-radio :label="1">润色</el-radio>
            <el-radio :label="0">不润色</el-radio>
          </el-radio-group>
        </el-form-item>

        <!-- 话题指令选择器 - 只在AI生成时显示 -->
        <el-form-item label="话题指令" prop="topic_instruct_id" v-if="titleForm.topic_type === 2">
          <el-select v-model="titleForm.topic_instruct_id" placeholder="请选择话题指令" filterable clearable
            style="width: 100%" :loading="loadingInstructs">
            <el-option label="默认指令" :value="-1" />
            <el-option v-for="instruct in instructList" :key="instruct.id" :label="instruct.instruct_name"
              :value="instruct.id" />
          </el-select>
        </el-form-item>

        <!-- 话题关键词 - 只在AI生成时显示 -->
        <el-form-item label="话题关键词" prop="topic_keyword" v-if="titleForm.topic_type === 2">
          <el-input v-model="titleForm.topic_keyword" type="textarea" :rows="3" placeholder="请输入话题关键词, 多个关键词用逗号分割！"
            size="large" />

          <div style="margin-top: 10px; display: flex; justify-content: flex-end;">
            <el-button type="primary" plain size="small" @click="addSeparator">
              <el-icon>
                <Plus />
              </el-icon>
              增加分隔符
            </el-button>
          </div>
        </el-form-item>

        <!-- 生成上限 - 只在AI生成时显示 -->
        <el-form-item label="生成上限" prop="topic_limit" v-if="titleForm.topic_type === 2">
          <el-input v-model="titleForm.topic_limit" placeholder="请输入生成上限" size="large" />
        </el-form-item>

        <el-form-item label="备注信息">
          <el-input v-model="titleForm.notes" placeholder="请输入备注信息" type="textarea" :rows="3" />
        </el-form-item>
      </el-form>

      <template #footer>
        <el-button @click="dialogVisible = false">取消</el-button>
        <el-button type="primary" @click="submitTitleForm" :loading="submitting">确认</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import axios from 'axios';
import config from '@/config.js';
import Cookies from 'js-cookie';
import {
  Search, Plus, Edit, Delete, View, MagicStick, InfoFilled
} from '@element-plus/icons-vue'
import TitleListView from '@/components/TopicListView.vue'
import AICreateView from '@/components/TAICreateView.vue'
import TitleAiCrateView from '@/components/TopicAiCrateView.vue'

export default {
  name: 'SeoconfigTopic',
  components: { Search, Plus, Edit, Delete, View, MagicStick, InfoFilled, TitleListView, AICreateView, TitleAiCrateView },
  data() {
    return {
      loading: false,
      submitting: false,
      dialogVisible: false,
      dialogTitle: '新增话题',
      isEditMode: false,
      instructList: [],
      loadingInstructs: false,
      aiConfigInfo: null,
      searchForm: {
        topic_name: ''
      },
      titleForm: {
        id: null,
        topic_type: 1,
        topic_name: '',
        topic_keyword: '',
        topic_limit: '',
        notes: '',
        topic_instruct_id: -1,
        polish_open: 1
      },
      formRules: {
        topic_name: [
          { required: true, message: '请输入话题名称', trigger: 'blur' }
        ],
        topic_type: [
          { required: true, message: '请选择写作类型', trigger: 'change' }
        ],
        topic_keyword: [
          { required: false, message: '请输入话题关键词', trigger: 'blur' }
        ],
        topic_limit: [
          { required: false, message: '请输入生成上限', trigger: 'blur' }
        ]
      },
      titleList: [],
      titleListDialogVisible: false,
      aiCreateDialogVisible: false,
      titleListDialogTitle: '',
      aiCreateDialogTitle: '',
      currentTitleId: null,
      currentTitleType: 1,
      pagination: {
        page: 1,
        pageSize: 10,
        total: 0
      }
    }
  },
  computed: {
    userInfo() {
      return JSON.parse(Cookies.get('user_info') || '{}')
    }
  },
  mounted() {
    this.fetchTitleList();
    this.fetchInstructList();
  },
  methods: {
    getAiSourceType(source) {
      const map = {
        'store': 'success',
        'agent_1': 'warning',
        'agent_2': 'primary',
        'admin': 'danger'
      };
      return map[source] || 'info';
    },

    getAiSourceText(source) {
      const map = {
        'store': '商户自有',
        'agent_1': '一级代理',
        'agent_2': '二级代理',
        'admin': '总后台'
      };
      return map[source] || source || '未知';
    },

    async fetchAIConfig() {
      try {
        const { data } = await axios.post(
          `${config.BASE_URL}/shop/api/topic_management.php`,
          {
            action: 'get_ai_config',
            store_id: this.userInfo.id
          }
        );
        if (data.success) {
          this.aiConfigInfo = data.data;
        } else {
          this.aiConfigInfo = null;
          if (this.titleForm.topic_type === 2) {
            this.$message.warning('AI配置获取失败: ' + (data.error || '未知错误'));
          }
        }
      } catch (error) {
        this.aiConfigInfo = null;
        console.error('获取AI配置失败:', error);
      }
    },

    handleTypeChange() {
      const isAIType = this.titleForm.topic_type === 2;

      if (isAIType) {
        this.titleForm.polish_open = 1;
        this.fetchAIConfig();
      }

      this.formRules.topic_keyword[0].required = isAIType;
      this.formRules.topic_limit[0].required = isAIType;

      this.$nextTick(() => {
        if (this.$refs.titleForm) {
          this.$refs.titleForm.clearValidate(['topic_keyword', 'topic_limit']);
        }
      });
    },

    async fetchInstructList() {
      this.loadingInstructs = true;
      try {
        const { data } = await axios.post(
          `${config.BASE_URL}/shop/api/CreationInstructionsController.php`,
          {
            action: 'list_all',
            store_id: this.userInfo.id
          }
        );

        if (data.data) {
          this.instructList = data.data.filter(instruct => instruct.instruct_type === 3);
        }
      } catch (error) {
        this.$message.error('获取话题指令失败');
        console.error('获取话题指令错误:', error);
      } finally {
        this.loadingInstructs = false;
      }
    },

    addSeparator() {
      if (this.titleForm.topic_keyword &&
        !this.titleForm.topic_keyword.trim().endsWith(',')) {
        this.titleForm.topic_keyword += ',';
      } else if (!this.titleForm.topic_keyword) {
        this.titleForm.topic_keyword = ',';
      }
    },

    async fetchTitleList() {
      this.loading = true;
      try {
        const params = {
          action: this.searchForm.topic_name ? 'search' : 'list',
          store_id: this.userInfo.id,
          page: this.pagination.page,
          page_size: this.pagination.pageSize
        };

        if (this.searchForm.topic_name) {
          params.topic_name = this.searchForm.topic_name;
        }

        const { data } = await axios.post(
          `${config.BASE_URL}/shop/api/topic_management.php`,
          params
        );

        if (data.data) {
          this.titleList = data.data;
          this.pagination.total = data.pagination?.total || data.data.length;
        }
      } catch (error) {
        this.$message.error('获取话题列表失败');
        console.error('获取话题列表错误:', error);
      } finally {
        this.loading = false;
      }
    },

    openTitleListDialog(row) {
      this.currentTitleId = row.id;
      this.currentTitleType = row.topic_type;
      this.titleListDialogTitle = `${row.topic_name} - 话题列表`;
      this.titleListDialogVisible = true;
    },

    openAICreateDialog(row) {
      this.currentTitleId = row.id;
      this.aiCreateDialogTitle = `${row.topic_name} - AI生成记录`;
      this.aiCreateDialogVisible = true;
    },

    handleSearch() {
      this.pagination.page = 1;
      this.fetchTitleList();
    },

    async openAddDialog() {
      this.dialogTitle = '新增话题';
      this.isEditMode = false;
      this.aiConfigInfo = null;
      this.titleForm = {
        id: null,
        topic_type: 1,
        topic_name: '',
        topic_keyword: '',
        topic_limit: '',
        notes: '',
        topic_instruct_id: -1,
        polish_open: 1
      };
      this.dialogVisible = true;
    },

    openEditDialog(row) {
      this.dialogTitle = '编辑话题';
      this.isEditMode = true;
      this.titleForm = {
        id: row.id,
        topic_type: row.topic_type,
        topic_name: row.topic_name,
        topic_keyword: row.topic_type === 2 ? row.topic_keyword : '',
        topic_limit: row.topic_limit,
        notes: row.notes,
        topic_instruct_id: row.topic_type === 2 ? (row.topic_instruct_id || -1) : -1,
        polish_open: row.polish_open ?? 1
      };

      if (row.topic_type === 2) {
        this.fetchAIConfig();
      } else {
        this.aiConfigInfo = null;
      }

      this.dialogVisible = true;
    },

    async submitTitleForm() {
      this.handleTypeChange();

      this.$refs.titleForm.validate(async (valid) => {
        if (valid) {
          this.submitting = true;
          try {
            const action = this.isEditMode ? 'update' : 'add';
            const payload = {
              ...this.titleForm,
              store_id: this.userInfo.id
            };

            if (this.titleForm.topic_type === 1) {
              payload.topic_keyword = '';
              payload.topic_limit = '';
              payload.topic_instruct_id = -1;
            }
            if (this.titleForm.topic_type === 2) {
              this.titleForm.polish_open = 1;
            }

            const { data } = await axios.post(
              `${config.BASE_URL}/shop/api/topic_management.php`,
              {
                action,
                ...payload
              }
            );

            if (data.success) {
              const aiSource = data.data?.ai_source;
              let msg = this.isEditMode ? '更新成功' : '添加成功';
              if (aiSource) {
                msg += `，AI来源：${this.getAiSourceText(aiSource)}`;
              }
              this.$message.success(msg);
              this.dialogVisible = false;
              this.fetchTitleList();
            } else {
              this.$message.error(data.error || '操作失败');
            }
          } catch (error) {
            const errorMsg = error.response?.data?.error || error.message || '操作失败';
            this.$message.error(`操作失败: ${errorMsg}`);
            console.error('表单提交错误:', error);
          } finally {
            this.submitting = false;
          }
        }
      });
    },

    async handleDelete(id) {
      this.$confirm('确定要删除该话题配置吗？', '警告', {
        confirmButtonText: '确认删除',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(async () => {
        try {
          const { data } = await axios.post(
            `${config.BASE_URL}/shop/api/topic_management.php`,
            {
              action: 'delete',
              id: id,
              store_id: this.userInfo.id
            }
          );

          if (data.success) {
            this.$message.success('删除成功');
            this.fetchTitleList();
          } else {
            this.$message.error(data.error || '删除失败');
          }
        } catch (error) {
          const errorMsg = error.response?.data?.error || error.message || '删除失败';
          this.$message.error(`删除失败: ${errorMsg}`);
          console.error('删除错误:', error);
        }
      }).catch(() => { });
    }
  }
}
</script>

<style scoped lang="scss">
.topic-management-page {
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  overflow: hidden;
}

.page-title {
  font-size: 24px;
  color: #1f2937;
  margin-bottom: 32px;
  font-weight: 600;
  text-align: center;
  letter-spacing: 0.5px;
}

.search-bar {
  display: flex;
  align-items: center;
  margin-bottom: 24px;
  padding: 16px;
  background: #f8fafc;
  border-radius: 8px;
  flex-wrap: wrap;
  gap: 10px;

  .add-btn {
    margin-left: auto;
  }
}

.pagination-container {
  margin-top: 24px;
  display: flex;
  justify-content: flex-end;
}

.table-wrapper {
  position: relative;
  width: 100%;
  overflow-x: auto;
  margin-top: 20px;
  -webkit-overflow-scrolling: touch;
}

.topic-table {
  :deep(.el-table__header) th {
    background-color: #f5f7fa;
    color: #606266;
    font-weight: 600;
    white-space: nowrap;
  }

  :deep(.el-table__body) tr:hover>td {
    background-color: #f0f7ff !important;
  }
  
  :deep(.el-table__body-wrapper) {
    overflow-x: auto !important;
    
    &::-webkit-scrollbar {
      height: 8px;
    }

    &::-webkit-scrollbar-track {
      background: #f1f1f1;
      border-radius: 4px;
    }

    &::-webkit-scrollbar-thumb {
      background: #c1c1c1;
      border-radius: 4px;

      &:hover {
        background: #a8a8a8;
      }
    }
  }
  
  :deep(.el-table__header-wrapper) {
    overflow: hidden !important;
    width: 100% !important;
  }
}

.el-button {
  display: inline-flex;
  align-items: center;

  .el-icon {
    margin-right: 5px;
  }
}

:deep(.el-dialog) {
  border-radius: 12px;

  .el-dialog__header {
    padding: 20px 24px;
    border-bottom: 1px solid #ebeef5;
    margin-right: 0;

    .el-dialog__title {
      font-weight: 600;
      color: #1f2937;
    }
  }

  .el-dialog__body {
    padding: 24px;
  }

  .el-dialog__footer {
    padding: 16px 24px;
    border-top: 1px solid #ebeef5;
  }
}

:deep(.el-form-item__error) {
  font-size: 12px;
  padding-top: 4px;
}

@media (max-width: 768px) {
  .search-bar {
    flex-direction: column;
    align-items: stretch;

    .el-input {
      width: 100% !important;
      margin-right: 0 !important;
    }

    .add-btn {
      margin-left: 0;
    }
  }

  .topic-table {
    font-size: 12px;
  }
}
</style>